import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Tache } from '../types';

interface GanttChartProps {
  taches: Tache[];
  onTacheClick?: (tache: Tache) => void;
}

type ZoomLevel = 'day' | 'week' | 'month' | 'year';

const GanttChart: React.FC<GanttChartProps> = ({ taches, onTacheClick }) => {
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('month');
  const [hoveredTache, setHoveredTache] = useState<Tache | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Calculer les dates min et max
  const { minDate, maxDate, dateRange } = useMemo(() => {
    if (taches.length === 0) {
      const today = new Date();
      return {
        minDate: today,
        maxDate: new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000),
        dateRange: 90
      };
    }

    const dates = taches.flatMap(t => [new Date(t.dateDebut), new Date(t.dateFin)]);
    const min = new Date(Math.min(...dates.map(d => d.getTime())));
    const max = new Date(Math.max(...dates.map(d => d.getTime())));
    
    // Ajouter une marge de 10%
    const range = max.getTime() - min.getTime();
    const margin = range * 0.1;
    min.setTime(min.getTime() - margin);
    max.setTime(max.getTime() + margin);
    
    const daysDiff = Math.ceil((max.getTime() - min.getTime()) / (1000 * 60 * 60 * 24));
    
    return { minDate: min, maxDate: max, dateRange: daysDiff };
  }, [taches]);

  // Largeur d'une unité selon le zoom
  const getUnitWidth = () => {
    switch (zoomLevel) {
      case 'day': return 60;
      case 'week': return 120;
      case 'month': return 40;
      case 'year': return 10;
      default: return 40;
    }
  };

  // Générer les colonnes de temps
  const timeColumns = useMemo(() => {
    const columns: { date: Date; label: string; isWeekend?: boolean }[] = [];
    // On démarre au début de la période calendaire qui contient minDate
    // (ex: 1er du mois, lundi de la semaine, 1er jan de l'année)
    // pour que les labels des colonnes correspondent exactement aux dates affichées.
    const current = getCalendarStart(new Date(minDate), zoomLevel);

    while (current <= maxDate) {
      const isWeekend = current.getDay() === 0 || current.getDay() === 6;
      
      switch (zoomLevel) {
        case 'day':
          columns.push({
            date: new Date(current),
            label: current.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
            isWeekend
          });
          current.setDate(current.getDate() + 1);
          break;
        case 'week':
          columns.push({
            date: new Date(current),
            label: `S${getWeekNumber(current)} - ${current.toLocaleDateString('fr-FR', { month: 'short' })}`
          });
          current.setDate(current.getDate() + 7);
          break;
        case 'month':
          columns.push({
            date: new Date(current),
            label: current.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })
          });
          current.setMonth(current.getMonth() + 1);
          break;
        case 'year':
          columns.push({
            date: new Date(current),
            label: current.getFullYear().toString()
          });
          current.setFullYear(current.getFullYear() + 1);
          break;
      }
    }
    
    return columns;
  }, [minDate, maxDate, zoomLevel]);

  // Convertit une date en position pixel en se basant sur les colonnes réelles.
  // Chaque colonne a une largeur fixe en px mais couvre une durée calendaire variable
  // (janvier = 31j, février = 28j, …). On interpole la position à l'intérieur de chaque
  // colonne proportionnellement à la durée réelle de cette colonne.
  const dateToPixel = (date: Date): number => {
    const unitWidth = getUnitWidth();
    if (timeColumns.length === 0) return 0;

    for (let i = 0; i < timeColumns.length; i++) {
      const colStart = timeColumns[i].date;
      // Fin exacte de la colonne = début de la colonne suivante (même calendrier)
      const colEnd =
        i + 1 < timeColumns.length
          ? timeColumns[i + 1].date
          : getNextColStart(timeColumns[i].date, zoomLevel);
      const colDurationMs = colEnd.getTime() - colStart.getTime();

      if (date.getTime() < colEnd.getTime()) {
        const fraction =
          colDurationMs > 0
            ? (date.getTime() - colStart.getTime()) / colDurationMs
            : 0;
        return (i + Math.max(0, fraction)) * unitWidth;
      }
    }

    return timeColumns.length * unitWidth;
  };

  const getTaskPosition = (tache: Tache) => {
    const leftPx = dateToPixel(new Date(tache.dateDebut));
    const rightPx = dateToPixel(new Date(tache.dateFin));
    return {
      left: `${leftPx}px`,
      width: `${Math.max(rightPx - leftPx, 10)}px`,
    };
  };

  // Obtenir la couleur selon l'état
  const getTaskColor = (tache: Tache) => {
    switch (tache.etat) {
      case 'terminee': return 'bg-green-500';
      case 'en_cours': return 'bg-blue-500';
      case 'en_attente': return 'bg-amber-500';
      default: return 'bg-gray-400';
    }
  };

  // Obtenir le numéro de semaine
  function getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  }

  // Retourne le début de la période calendaire contenant `date` pour ce zoom
  function getCalendarStart(date: Date, zoom: ZoomLevel): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    switch (zoom) {
      case 'day':
        return d;
      case 'week': {
        const dow = d.getDay(); // 0 = dim, 1 = lun …
        d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1)); // recule au lundi
        return d;
      }
      case 'month':
        d.setDate(1);
        return d;
      case 'year':
        d.setMonth(0, 1);
        return d;
    }
  }

  // Retourne la date de début de la colonne SUIVANTE (= fin exacte d'une colonne)
  function getNextColStart(date: Date, zoom: ZoomLevel): Date {
    const d = new Date(date);
    switch (zoom) {
      case 'day':   d.setDate(d.getDate() + 1); break;
      case 'week':  d.setDate(d.getDate() + 7); break;
      case 'month': d.setMonth(d.getMonth() + 1); break;
      case 'year':  d.setFullYear(d.getFullYear() + 1); break;
    }
    return d;
  }

  // Gestion du drag pour scroll horizontal
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Gestion du tooltip
  const handleTacheHover = (e: React.MouseEvent, tache: Tache) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setHoveredTache(tache);
  };

  // Formater la date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Calculer la durée en jours
  const getDuration = (dateDebut: string, dateFin: string) => {
    const start = new Date(dateDebut);
    const end = new Date(dateFin);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Traduire l'état
  const translateEtat = (etat: string) => {
    switch (etat) {
      case 'terminee': return 'Terminée';
      case 'en_cours': return 'En cours';
      case 'en_attente': return 'En attente';
      default: return etat;
    }
  };

  // Traduire la complexité
  const translateComplexite = (complexite: string) => {
    switch (complexite) {
      case 'facile': return 'Facile';
      case 'moyen': return 'Moyen';
      case 'difficile': return 'Difficile';
      default: return complexite;
    }
  };

  const totalWidth = timeColumns.length * getUnitWidth();

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header avec contrôles de zoom */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-800">Diagramme de Gantt</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 mr-2">Zoom:</span>
          <div className="flex rounded-lg overflow-hidden border border-gray-300">
            {(['day', 'week', 'month', 'year'] as ZoomLevel[]).map((level) => (
              <button
                key={level}
                onClick={() => setZoomLevel(level)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  zoomLevel === level
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {level === 'day' ? 'Jour' : level === 'week' ? 'Semaine' : level === 'month' ? 'Mois' : 'Année'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Légende */}
      <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 border-b text-sm">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          <span className="text-gray-600">Terminée</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-500"></div>
          <span className="text-gray-600">En cours</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-amber-500"></div>
          <span className="text-gray-600">En attente</span>
        </div>
        <div className="flex items-center gap-1 ml-auto text-gray-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
          <span>Glisser pour naviguer</span>
        </div>
      </div>

      {/* Container scrollable */}
      <div
        ref={containerRef}
        className={`overflow-x-auto ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ maxHeight: '500px' }}
      >
        <div style={{ minWidth: `${totalWidth}px` }}>
          {/* Timeline header */}
          <div className="flex border-b bg-gray-100 sticky top-0 z-10">
            <div className="w-48 min-w-48 p-2 font-medium text-gray-700 border-r bg-gray-100 sticky left-0 z-20">
              Tâche
            </div>
            <div className="flex flex-1">
              {timeColumns.map((col, idx) => (
                <div
                  key={idx}
                  className={`text-center text-xs font-medium py-2 border-r ${
                    col.isWeekend ? 'bg-gray-200 text-gray-500' : 'text-gray-600'
                  }`}
                  style={{ width: `${getUnitWidth()}px`, minWidth: `${getUnitWidth()}px` }}
                >
                  {col.label}
                </div>
              ))}
            </div>
          </div>

          {/* Tâches */}
          {taches.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Aucune tâche à afficher
            </div>
          ) : (
            taches.map((tache, index) => {
              const position = getTaskPosition(tache);
              return (
                <div
                  key={tache.id}
                  className={`flex border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                >
                  {/* Nom de la tâche */}
                  <div className="w-48 min-w-48 p-2 border-r bg-white sticky left-0 z-10">
                    <div className="text-sm font-medium text-gray-800 truncate" title={tache.nom}>
                      {tache.nom}
                    </div>
                    <div className="text-xs text-gray-500">
                      {translateComplexite(tache.complexite)}
                    </div>
                  </div>
                  
                  {/* Barre de Gantt */}
                  <div className="flex-1 relative h-14 flex items-center">
                    {/* Grille de fond */}
                    <div className="absolute inset-0 flex">
                      {timeColumns.map((col, idx) => (
                        <div
                          key={idx}
                          className={`border-r ${col.isWeekend ? 'bg-gray-100/50' : ''}`}
                          style={{ width: `${getUnitWidth()}px`, minWidth: `${getUnitWidth()}px` }}
                        />
                      ))}
                    </div>
                    
                    {/* Barre de la tâche */}
                    <div
                      className={`absolute h-8 rounded-md shadow-sm ${getTaskColor(tache)} cursor-pointer
                        hover:shadow-md hover:brightness-110 transition-all duration-200`}
                      style={{
                        left: position.left,
                        width: position.width,
                        minWidth: '20px'
                      }}
                      onMouseEnter={(e) => handleTacheHover(e, tache)}
                      onMouseLeave={() => setHoveredTache(null)}
                      onClick={() => onTacheClick?.(tache)}
                    >
                      <div className="px-2 py-1 text-xs text-white font-medium truncate">
                        {tache.nom}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Tooltip */}
      {hoveredTache && (
        <div
          className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-sm pointer-events-none"
          style={{
            left: `${Math.min(tooltipPosition.x, window.innerWidth - 320)}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="font-semibold text-gray-900 mb-2">{hoveredTache.nom}</div>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${getTaskColor(hoveredTache)}`}>
                {translateEtat(hoveredTache.etat)}
              </span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-600">{translateComplexite(hoveredTache.complexite)}</span>
            </div>
            
            <div className="text-gray-600">
              <div className="flex justify-between">
                <span>Début:</span>
                <span className="font-medium">{formatDate(hoveredTache.dateDebut)}</span>
              </div>
              <div className="flex justify-between">
                <span>Fin:</span>
                <span className="font-medium">{formatDate(hoveredTache.dateFin)}</span>
              </div>
              <div className="flex justify-between">
                <span>Durée:</span>
                <span className="font-medium">{getDuration(hoveredTache.dateDebut, hoveredTache.dateFin)} jours</span>
              </div>
            </div>
            
            {hoveredTache.description && (
              <div className="pt-2 border-t border-gray-100">
                <div className="text-gray-500 text-xs mb-1">Description:</div>
                <div className="text-gray-700">{hoveredTache.description}</div>
              </div>
            )}
            
            {hoveredTache.livrables && (
              <div className="pt-2 border-t border-gray-100">
                <div className="text-gray-500 text-xs mb-1">Livrables:</div>
                <div className="text-gray-700">{hoveredTache.livrables}</div>
              </div>
            )}
            
            {hoveredTache.enRetard && (
              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center gap-1 text-red-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Tâche en retard</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Flèche du tooltip */}
          <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full">
            <div className="w-3 h-3 bg-white border-r border-b border-gray-200 transform rotate-45 -mt-1.5"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GanttChart;
